//@flow
import React, {useState, useEffect} from 'react';
import {makeStyles, Typography, Grid, IconButton, Box} from '@material-ui/core';
import {colors} from '../../../assets/styles/colors';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../elements/CustomTextField/CustomTextField';
import {useChatBotsActions} from '../hooks';
import {useToastActions} from '../../toast-alert/utils';
import {generateChatGPTUrl} from '../utils';
import {
  PromptHelpModal,
  PromptHelpIconButton,
} from './prompt-help/PromptHelpModal';

import type {StoreChatbot} from '../../../entities';
import SparkIcon from '../../../elements/icons/SparkIcon';
import TiendadaTooltip from '../../../elements/tiendada-tooltip';

const useStyles = makeStyles(theme => ({
  sectionCard: {
    padding: '1rem',
    [theme.breakpoints.down('xs')]: {
      padding: '.5rem',
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    position: 'relative',
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  promptHelperContainer: {
    marginBottom: '.75rem',
  },
  promptHelperTitle: {
    fontSize: '.875rem',
    fontWeight: 600,
    color: colors.grey700,
    marginBottom: '.25rem',
  },
  promptHelperText: {
    fontSize: '.8125rem',
    color: colors.grey600,
  },
  textEditorContainer: {
    position: 'relative',
  },
  iconButton: {
    position: 'absolute',
    top: '.25rem',
    right: '.25rem',
    zIndex: 1,
    opacity: 0.6,
    '&:hover': {
      opacity: 1,
    },
  },
  sectionFooter: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '1rem',
  },
  submitButton: {
    maxWidth: '200px',
  },
  promptTextField: {
    '& textarea': {
      resize: 'vertical',
    },
  },
}));

type Props = {
  chatBot: StoreChatbot,
  onSaveSuccess: () => void,
};

const BotPromptSection = React.memo(function BotPromptSection({
  chatBot,
  onSaveSuccess,
}: Props) {
  const classes = useStyles();
  const {editChatBot} = useChatBotsActions();
  const {showToast} = useToastActions();

  const [prompt, setPrompt] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPromptHelpOpen, setIsPromptHelpOpen] = useState(false);

  useEffect(() => {
    if (chatBot?.prompt) {
      setPrompt(chatBot.prompt);
    }
  }, [chatBot.prompt]);

  const handleSavePrompt = async () => {
    if (!chatBot?.id) return;

    if (!prompt.trim()) {
      showToast({
        message: 'El prompt no puede estar vacío',
        title: 'Prompt requerido',
        type: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      setIsSaving(true);
      await editChatBot(chatBot.id, {prompt});
      showToast({
        message: 'Prompt actualizado correctamente',
        title: 'Cambios guardados',
        type: 'success',
        duration: 3000,
      });

      onSaveSuccess();
    } catch (error) {
      showToast({
        message: 'Error al actualizar el prompt',
        title: 'Error',
        type: 'error',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid item xs={12}>
      <section className={classes.sectionCard}>
        <Box display="flex" alignItems="center" style={{gap: '.25rem'}} mb={1}>
          <header className={classes.sectionHeader}>
            <Typography className={classes.subtitle}>
              {chatBot?.name}
            </Typography>
          </header>

          <PromptHelpIconButton
            onClick={() => {
              setIsPromptHelpOpen(true);
            }}
          />
        </Box>

        <div className={classes.textEditorContainer}>
          <TiendadaTooltip title="Optimizar prompt">
            <IconButton
              color="primary"
              className={classes.iconButton}
              onClick={() => window.open(generateChatGPTUrl(prompt), '_blank')}
              disabled={!prompt.trim()}
            >
              <SparkIcon />
            </IconButton>
          </TiendadaTooltip>

          <CustomTextField
            value={prompt}
            multiline
            label="Prompt del bot"
            maxRows={10}
            minRows={4}
            classes={{
              root: classes.promptTextField,
            }}
            onChange={e => setPrompt(e.target.value)}
          />
        </div>

        <footer className={classes.sectionFooter}>
          <CustomButton
            variant="contained"
            color="primary"
            className={classes.submitButton}
            onClick={handleSavePrompt}
            disabled={isSaving}
          >
            {isSaving ? 'Actualizando...' : 'Actualizar prompt'}
          </CustomButton>
        </footer>

        <PromptHelpModal
          open={isPromptHelpOpen}
          onClose={() => {
            setIsPromptHelpOpen(false);
          }}
        />
      </section>
    </Grid>
  );
});

export default BotPromptSection;
